import { useState } from 'react'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

interface ChangePasswordResponse {
  success: boolean
  message: string
}

const API_URL =
  import.meta.env.VITE_EPC_API_URL ?? import.meta.env.REACT_APP_API_URL ?? ''

/**
 * Custom hook to handle password changes
 * @returns Mutation function and loading/error states
 */
export const useChangePasswordMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
    newPasswordConfirm: string
  ): Promise<ChangePasswordResponse> => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetchWithAuth(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword,
          newPasswordConfirm,
          newPassword,
        }),
      })

      setLoading(false)
      return {
        success: true,
        message: response.message || 'Password changed successfully',
      }
    } catch (err) {
      setLoading(false)
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      return {
        success: false,
        message: errorMessage,
      }
    }
  }

  return {
    changePassword,
    loading,
    error,
    clearError: () => setError(null),
  }
}
